import { Injectable, type Provider } from '@angular/core';

import { ok, type ApiResult } from '../../../core/models/api-result';
import type { SavedEntry, SavedPage } from './hub-saved.models';
import { HubSavedService } from './hub-saved.service';

/**
 * SAVED, WITHOUT A SESSION — for `/preview/hubs?saved=1`.
 *
 * `myStarredContent` is viewer-scoped, so unauthenticated it 401s and Saved
 * would be an error banner. This answers the one read seam from an in-memory
 * shelf (`hub-saved-preview.corpus.ts`) through the real service's paging,
 * unsave and undo. The writes already go through the preview engagement
 * service on that route, so a star changes the harness's copy only.
 *
 * Small pages (8) so infinite paging is reviewable, and a short delay so the
 * skeletons are. `?saved=empty` serves an empty shelf for the empty state.
 *
 * ⚠ LAZY: `app.routes.ts` imports this PROVIDER as a value, so the fixtures
 * and the corpus are imported inside the method.
 */
export const SAVED_PREVIEW_PAGE = 8;

@Injectable()
export class HubSavedPreviewService extends HubSavedService {
  private shelf: Promise<SavedEntry[]> | null = null;

  private all(): Promise<SavedEntry[]> {
    this.shelf ??= Promise.all([
      import('../data/hubs-preview.fixtures'),
      import('./hub-saved-preview.corpus'),
    ]).then(([fixtures, corpus]) =>
      isEmptyShelf() ? [] : corpus.savedPreviewShelf(fixtures.HUBS_PREVIEW_ITEMS, Date.now()),
    );
    return this.shelf;
  }

  override load(): Promise<void> {
    return super.load(SAVED_PREVIEW_PAGE);
  }

  override loadMore(): Promise<void> {
    return super.loadMore(SAVED_PREVIEW_PAGE);
  }

  protected override async fetchPage(first: number, after: string | null): Promise<ApiResult<SavedPage | null>> {
    const shelf = await this.all();
    await new Promise(resolve => setTimeout(resolve, 260));
    const start = after ? shelf.findIndex(entry => entry.cursor === after) + 1 : 0;
    const entries = shelf.slice(start, start + first);
    const last = entries.at(-1) ?? null;
    return ok({
      entries,
      totalCount: shelf.length,
      hasNextPage: start + first < shelf.length,
      endCursor: last?.cursor ?? null,
      truncated: false,
    });
  }
}

function isEmptyShelf(): boolean {
  return typeof location !== 'undefined' && /[?&]saved=empty\b/.test(location.hash + location.search);
}

export const HUB_SAVED_PREVIEW_PROVIDER: Provider = {
  provide: HubSavedService,
  useClass: HubSavedPreviewService,
};
